import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Sparkles, Map } from "lucide-react";
import ROADMAP, { getRoadmapStats } from "../data/roadmap";

export default function Roadmap() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const stats = getRoadmapStats();

  const doneItems = ROADMAP.filter(m => m.status === "done");
  const inProgressItems = ROADMAP.filter(m => m.status === "in_progress");
  const upcomingItems = ROADMAP.filter(m => m.status === "upcoming");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── HERO (cohérent avec Home) ────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-900">
        <div
          className="pointer-events-none absolute inset-0 opacity-80"
          style={{
            background: "radial-gradient(circle at 0% 0%, rgba(129,140,248,0.45) 0, transparent 55%), radial-gradient(circle at 90% 10%, rgba(236,72,153,0.22) 0, transparent 55%), radial-gradient(circle at 50% 100%, rgba(56,189,248,0.16) 0, transparent 60%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(circle at 50% 35%, black 0%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(circle at 50% 35%, black 0%, transparent 70%)",
          }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.28em] text-indigo-300 uppercase mb-4">
              <Map className="h-3.5 w-3.5" />
              Roadmap
            </span>

            {/* Titre stylé Dislyte mais cohérent */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white tracking-tight mb-3 leading-none">
              Notre trajectoire
            </h1>
            <div className="text-base sm:text-lg font-bold text-orange-300 tracking-[0.4em] mb-6">
              2025 - 2027+
            </div>

            <p className="text-sm sm:text-base text-indigo-100/85 leading-relaxed mb-8 max-w-2xl mx-auto">
              D'où l'on vient, où l'on va. Pour vous donner toute la confiance dont vous avez besoin.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <StatBadge icon={<CheckCircle2 className="h-4 w-4" />} label="livrés" value={stats.done} color="emerald" />
              <StatBadge icon={<Loader2 className="h-4 w-4 animate-spin" />} label="en cours" value={stats.inProgress} color="orange" />
              <StatBadge icon={<Sparkles className="h-4 w-4" />} label="à venir" value={stats.upcoming} color="indigo" />
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION ACQUIS ───────────────────────────────────────────── */}
      <SectionHeader
        label="Livré"
        title="Ce qui a déjà été livré"
        subtitle="Du prototype au lancement public, voilà le chemin parcouru."
        accentColor="emerald"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {doneItems.map((m, i) => <DislyteCard key={m.id} m={m} index={i} />)}
        </div>
      </div>

      {/* ── SECTION EN COURS ─────────────────────────────────────────── */}
      <SectionHeader
        label="EN COURS"
        title="Ce qui se construit en ce moment"
        subtitle="Vous voyez les chantiers actifs et ce qui arrive prochainement."
        accentColor="orange"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {inProgressItems.map((m, i) => <DislyteCard key={m.id} m={m} index={i + doneItems.length} />)}
        </div>
      </div>

      {/* ── SECTION À VENIR ──────────────────────────────────────────── */}
      <SectionHeader
        label="À VENIR"
        title="Ce qui arrive ensuite"
        subtitle="La direction qu'on prend pour 2027 et au-delà."
        accentColor="indigo"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {upcomingItems.map((m, i) => <DislyteCard key={m.id} m={m} index={i + doneItems.length + inProgressItems.length} />)}
        </div>
      </div>

      {/* ── CTA FINAL ────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden py-16 bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-900">
        <div
          className="pointer-events-none absolute inset-0 opacity-50"
          style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(249, 115, 22, 0.25) 0, transparent 60%), radial-gradient(ellipse at 70% 50%, rgba(99, 102, 241, 0.25) 0, transparent 60%)" }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Vous voulez faire partie de l'aventure ?
          </h2>
          <p className="text-sm sm:text-base text-indigo-100/85 mb-8 max-w-xl mx-auto">
            Que vous soyez une entreprise qui cherche un bon team building ou un partenaire prêt à recommander, on a une place pour vous.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/catalogue"
              className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 px-7 py-3 text-sm font-bold text-white shadow-md shadow-indigo-500/30 ring-1 ring-white/10 transition"
            >
              Voir les jeux disponibles
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/devenir-partenaire"
              className="inline-flex items-center gap-2 rounded-full border border-indigo-300/60 hover:border-indigo-200 hover:bg-indigo-900/40 px-7 py-3 text-sm font-bold text-indigo-100 transition"
            >
              Devenir partenaire
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// COMPOSANTS
// ─────────────────────────────────────────────────────────────────────

function StatBadge({ icon, label, value, color }) {
  const colorMap = {
    emerald: "border-emerald-300/40 bg-emerald-500/10 text-emerald-200",
    orange: "border-orange-300/40 bg-orange-500/10 text-orange-200",
    indigo: "border-indigo-300/40 bg-indigo-500/10 text-indigo-200",
  };
  return (
    <div className={`inline-flex items-center gap-2.5 rounded-full border px-5 py-2.5 backdrop-blur ${colorMap[color]}`}>
      {icon}
      <span className="text-2xl font-black">{value}</span>
      <span className="text-[11px] font-semibold tracking-[0.15em] uppercase opacity-90">{label}</span>
    </div>
  );
}

function SectionHeader({ label, title, subtitle, accentColor }) {
  const colorMap = {
    emerald: "text-emerald-600",
    orange: "text-orange-600",
    indigo: "text-indigo-600",
  };
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
      <div className="text-center">
        <span className={`inline-block text-[11px] font-bold tracking-[0.28em] uppercase mb-3 ${colorMap[accentColor]}`}>
          {label}
        </span>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-3">
          {title}
        </h2>
        <p className="text-sm text-slate-500 max-w-xl mx-auto">{subtitle}</p>
      </div>
    </div>
  );
}

// ─── Card style Dislyte mais en thème clair ────────────────────────
function DislyteCard({ m, index }) {
  const isDone = m.status === "done";
  const isInProgress = m.status === "in_progress";
  const isUpcoming = m.status === "upcoming";

  const config = isDone
    ? {
        bandBg: "bg-gradient-to-r from-emerald-500 to-emerald-600",
        bandText: "✓ Livré",
        accent: "#10b981",
        accentLight: "rgba(16, 185, 129, 0.15)",
        bullet: "bg-emerald-500",
        borderHover: "hover:border-emerald-300",
        shadowHover: "hover:shadow-emerald-100",
        periodText: "text-emerald-700 bg-emerald-50",
      }
    : isInProgress
    ? {
        bandBg: "bg-gradient-to-r from-orange-500 to-pink-500",
        bandText: "⚡ EN COURS",
        accent: "#f97316",
        accentLight: "rgba(249, 115, 22, 0.18)",
        bullet: "bg-orange-500",
        borderHover: "hover:border-orange-300",
        shadowHover: "hover:shadow-orange-100",
        periodText: "text-orange-700 bg-orange-50",
      }
    : {
        bandBg: "bg-gradient-to-r from-indigo-500 to-purple-500",
        bandText: "✨ À VENIR",
        accent: "#6366f1",
        accentLight: "rgba(99, 102, 241, 0.15)",
        bullet: "bg-indigo-500",
        borderHover: "hover:border-indigo-300",
        shadowHover: "hover:shadow-indigo-100",
        periodText: "text-indigo-700 bg-indigo-50",
      };

  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-2xl bg-white border border-slate-200 ${config.borderHover} shadow-sm hover:shadow-xl ${config.shadowHover} transition-all duration-300 hover:-translate-y-1.5`}
    >
      {/* Bandeau statut en haut (style Dislyte) */}
      <div className={`${config.bandBg} px-4 py-2 text-center relative overflow-hidden`}>
        <span className="relative text-[10px] font-black tracking-[0.2em] text-white uppercase">
          {config.bandText}
        </span>
        {/* Brillance subtile */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)",
          }}
        />
      </div>

      {/* Image avec overlay */}
      <div className="relative overflow-hidden h-56">
        <img
          src={m.image}
          alt={m.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          style={isUpcoming ? { filter: "saturate(0.75) brightness(0.95)" } : { filter: "saturate(1.05)" }}
        />

        {/* Overlay coloré subtil */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${config.accentLight} 0%, transparent 60%, rgba(0,0,0,0.4) 100%)`,
          }}
        />

        {/* Numéro index discret */}
        <div className="absolute top-3 right-3 z-10 text-4xl font-black text-white/40 leading-none select-none">
          {String(index + 1).padStart(2, "0")}
        </div>

        {/* Période en bas de l'image */}
        <div className="absolute bottom-3 left-3 z-10">
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold tracking-[0.1em] uppercase ${config.periodText} shadow-sm`}>
            {m.period}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="relative flex flex-col flex-1 p-5">
        {/* Titre style Dislyte (épais + uppercase) */}
        <h3 className="text-xl font-black text-slate-900 mb-3 tracking-tight leading-tight">
          {m.title}
        </h3>

        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          {m.description}
        </p>

        {/* Highlights */}
        {m.highlights?.length > 0 && (
          <ul className="space-y-2 mt-auto pt-4 border-t border-slate-100">
            {m.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2.5 text-xs text-slate-500">
                <span className={`mt-1.5 inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${config.bullet}`} />
                <span className="leading-relaxed">{h}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Liseré coloré en bas (apparait au hover) */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `linear-gradient(90deg, transparent, ${config.accent}, transparent)` }}
      />
    </article>
  );
}